import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';

import { ApiError } from '../../shared/api/client.ts';
import {
  baixarCertificado,
  useGuardarCertificado,
  useRemoverCertificado,
} from '../../shared/api/queries.ts';
import { useSessao } from '../auth/sessao.tsx';
import { situacaoDoCertificado, type Cliente } from '../../shared/api/tipos.ts';
import { formatarData, formatarDataHora } from '../../shared/format.ts';

/** O mesmo teto do servidor, só para a recusa ser imediata. */
const TAMANHO_MAXIMO_BYTES = 256 * 1024;

/**
 * O certificado A1 do cliente, dentro do cadastro dele.
 *
 * Ele fica aqui, e não numa tela própria, porque é um dado do cliente como o
 * CNPJ e os e-mails: quem vai anexá-lo acabou de receber o arquivo da loja e
 * está com o cadastro aberto na frente.
 *
 * O envio **não** espera o botão Salvar do diálogo: é `multipart`, acontece na
 * hora e vale sozinho. O resto do formulário é texto que só vira verdade ao
 * salvar; o certificado é um arquivo que ou subiu e abriu, ou não subiu.
 */
export function SecaoCertificado({ cliente }: { cliente: Cliente | undefined }) {
  const { usuario } = useSessao();
  // Baixar é a única ação que tira o `.pfx` do sistema, e a rota é de
  // administrador — o botão some para os outros em vez de responder 403.
  const souAdministrador = usuario?.papel === 'administrador';

  const guardar = useGuardarCertificado();
  const remover = useRemoverCertificado();

  const [arquivo, setArquivo] = useState<File | null>(null);
  const [senha, setSenha] = useState('');
  const [trocando, setTrocando] = useState(false);
  const [confirmandoRemocao, setConfirmandoRemocao] = useState(false);
  const [erroLocal, setErroLocal] = useState<string | null>(null);

  const certificado = cliente?.certificado ?? null;
  const ocupado = guardar.isPending || remover.isPending;

  function limpar() {
    setArquivo(null);
    setSenha('');
    setTrocando(false);
    setErroLocal(null);
    guardar.reset();
    remover.reset();
  }

  function escolher(selecionado: File | null) {
    setErroLocal(null);
    guardar.reset();

    if (selecionado !== null && selecionado.size > TAMANHO_MAXIMO_BYTES) {
      setArquivo(null);
      setErroLocal('O arquivo é grande demais para um certificado .pfx.');
      return;
    }

    setArquivo(selecionado);
  }

  function enviar() {
    if (cliente === undefined || arquivo === null || senha === '') return;
    guardar.mutate({ id: cliente.id, arquivo, senha }, { onSuccess: limpar });
  }

  return (
    <Box>
      <Divider sx={{ mb: 2 }} />

      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
        <WorkspacePremiumOutlinedIcon fontSize="small" color="action" />
        <Typography variant="subtitle2">Certificado digital</Typography>
      </Stack>

      {cliente === undefined ? (
        <Typography variant="body2" color="text.secondary">
          Cadastre o cliente primeiro; o certificado é anexado depois, na edição.
        </Typography>
      ) : (
        <Stack spacing={2}>
          {(guardar.isError || remover.isError || erroLocal !== null) && (
            <Alert severity="error" onClose={() => { setErroLocal(null); guardar.reset(); remover.reset(); }}>
              {erroLocal ?? mensagemDoErro(guardar.error ?? remover.error)}
            </Alert>
          )}

          {certificado !== null && !trocando ? (
            <Box
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                p: 1.5,
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                sx={{ alignItems: 'center', flexWrap: 'wrap', mb: 0.5 }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {certificado.titular}
                </Typography>
                <ChipDeValidade validoAte={certificado.validoAte} />
              </Stack>

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {certificado.cnpjFormatado ?? 'Sem CNPJ no titular'} · {certificado.nomeArquivo}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Válido de {formatarData(certificado.validoDe)} a{' '}
                {formatarData(certificado.validoAte)} · anexado em{' '}
                {formatarDataHora(certificado.enviadoEm)}
              </Typography>

              {confirmandoRemocao ? (
                <Alert
                  severity="warning"
                  sx={{ mt: 1.5 }}
                  action={
                    <Stack direction="row" spacing={1}>
                      <Button
                        type="button"
                        size="small"
                        onClick={() => setConfirmandoRemocao(false)}
                        disabled={ocupado}
                      >
                        Cancelar
                      </Button>
                      <Button
                        type="button"
                        size="small"
                        color="error"
                        variant="contained"
                        loading={remover.isPending}
                        onClick={() =>
                          remover.mutate(cliente.id, {
                            onSuccess: () => {
                              setConfirmandoRemocao(false);
                              limpar();
                            },
                          })
                        }
                      >
                        Apagar
                      </Button>
                    </Stack>
                  }
                >
                  Apagar o certificado e a senha guardada? Para usá-lo de novo será preciso
                  pedir o arquivo à loja.
                </Alert>
              ) : (
                <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap' }}>
                  {souAdministrador && (
                    <Button
                      type="button"
                      size="small"
                      startIcon={<DownloadOutlinedIcon />}
                      onClick={() => void baixarCertificado(cliente.id)}
                    >
                      Baixar
                    </Button>
                  )}
                  <Button
                    type="button"
                    size="small"
                    startIcon={<UploadFileOutlinedIcon />}
                    onClick={() => setTrocando(true)}
                  >
                    Substituir
                  </Button>
                  <Button
                    type="button"
                    size="small"
                    color="error"
                    startIcon={<DeleteOutlinedIcon />}
                    onClick={() => setConfirmandoRemocao(true)}
                  >
                    Apagar
                  </Button>
                </Stack>
              )}
            </Box>
          ) : (
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                <Button
                  type="button"
                  component="label"
                  variant="outlined"
                  size="small"
                  startIcon={<UploadFileOutlinedIcon />}
                  disabled={ocupado}
                >
                  Escolher arquivo
                  <input
                    hidden
                    type="file"
                    accept=".pfx,.p12,application/x-pkcs12"
                    onChange={(evento) => escolher(evento.target.files?.[0] ?? null)}
                  />
                </Button>
                <Typography variant="body2" color="text.secondary">
                  {arquivo?.name ?? 'Nenhum arquivo escolhido'}
                </Typography>
              </Stack>

              <TextField
                label="Senha do certificado"
                type="password"
                value={senha}
                onChange={(evento) => setSenha(evento.target.value)}
                size="small"
                fullWidth
                autoComplete="off"
                // O diálogo inteiro é um `<form>`: sem isto, Enter aqui
                // salvaria o cadastro em vez de enviar o certificado.
                onKeyDown={(evento) => {
                  if (evento.key !== 'Enter') return;
                  evento.preventDefault();
                  enviar();
                }}
                helperText="A senha é guardada cifrada no servidor, e não volta a aparecer em tela nenhuma."
              />

              <Stack direction="row" spacing={1}>
                <Button
                  type="button"
                  variant="contained"
                  size="small"
                  disabled={arquivo === null || senha === ''}
                  loading={guardar.isPending}
                  onClick={enviar}
                >
                  Guardar certificado
                </Button>
                {trocando && (
                  <Button type="button" size="small" onClick={limpar} disabled={ocupado}>
                    Cancelar
                  </Button>
                )}
              </Stack>
            </Stack>
          )}
        </Stack>
      )}
    </Box>
  );
}

/**
 * Verde, âmbar ou vermelho, derivado da data — o A1 vale um ano, e renovar
 * exige agendar validação com a certificadora. Um chip que diz só "válido"
 * até o dia do vencimento avisaria tarde demais.
 */
function ChipDeValidade({ validoAte }: { validoAte: string }) {
  const { situacao, dias } = situacaoDoCertificado({ validoAte });

  if (situacao === 'vencido') {
    return <Chip size="small" color="error" label="Vencido" />;
  }
  if (situacao === 'vencendo') {
    return (
      <Chip size="small" color="warning" label={`Vence em ${dias} dia${dias === 1 ? '' : 's'}`} />
    );
  }
  return <Chip size="small" color="success" variant="outlined" label="Válido" />;
}

function mensagemDoErro(erro: unknown): string {
  if (erro instanceof ApiError) return erro.message;
  return erro instanceof Error ? erro.message : 'Não foi possível guardar o certificado.';
}
